/**
 * Reflects two pieces of cart-drawer state as classes on <html>, so CSS can
 * respond to them without guessing.
 *
 *   cart-drawer-open      the cart drawer is open
 *   cart-coupon-focused   the discount field inside it has focus
 *
 * Why classes rather than CSS alone:
 *
 * `body.modal-show` looks like the obvious hook but is shared by every modal and
 * driven by a counter, so if any modal ever shows without a matching hide the
 * class sticks and whatever depends on it stays applied for the rest of the
 * session. Observing this one drawer's own `open` attribute has no such coupling.
 *
 * The attribute is set in show() and removed in hide() on the drawer element
 * itself, so a MutationObserver reflects the state immediately — no waiting for
 * transition events, and no dependence on :has() support.
 *
 * Cost: the focus listeners are bound only while the drawer is open, so ordinary
 * browsing carries no document-level focus handlers at all — just one
 * MutationObserver on a single attribute of a single element. They stay delegated
 * from document rather than bound to the input, because cart.js replaces the
 * drawer footer's innerHTML on every cart update and would discard a listener
 * attached to the element itself.
 */
(() => {
  const DRAWER_ID = 'CartDrawer';
  const FIELD = '.cart-discount__form--inline input';
  const OPEN_CLASS = 'cart-drawer-open';
  const FOCUS_CLASS = 'cart-coupon-focused';

  const root = document.documentElement;
  const isField = (el) => el instanceof Element && typeof el.matches === 'function' && el.matches(FIELD);

  const onFocusIn = (event) => {
    if (isField(event.target)) root.classList.add(FOCUS_CLASS);
  };
  const onFocusOut = (event) => {
    if (isField(event.target)) root.classList.remove(FOCUS_CLASS);
  };

  let listening = false;
  const listen = (shouldListen) => {
    if (shouldListen === listening) return;
    const method = shouldListen ? 'addEventListener' : 'removeEventListener';
    document[method]('focusin', onFocusIn);
    document[method]('focusout', onFocusOut);
    listening = shouldListen;
  };

  const start = () => {
    const drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;

    const sync = () => {
      const open = drawer.hasAttribute('open');
      root.classList.toggle(OPEN_CLASS, open);
      listen(open);
      // Closing the drawer while the field still holds focus must not leave the
      // focus state applied.
      if (!open) root.classList.remove(FOCUS_CLASS);
    };

    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['open'] });

    // If the drawer ever left the DOM while open, the attribute observer would
    // never fire again and .cart-drawer-open would stay applied — keeping the
    // Klaviyo rule active for good. Watching the parent closes that off.
    if (drawer.parentNode) {
      new MutationObserver(() => {
        if (!drawer.isConnected) {
          root.classList.remove(OPEN_CLASS);
          root.classList.remove(FOCUS_CLASS);
        }
      }).observe(drawer.parentNode, { childList: true });
    }
    sync();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
